import { useState, useEffect } from 'react';
import { X, Edit2, Save, Calendar, User, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { consultationsService } from '@/services/api/consultations.service';
import { Modal } from '@/components/ui/Modal';
import { MedicationManager, type SelectedMedication } from './MedicationManager';
import type { Appointment } from '@/types';
import type { ConsultationWithMedications, Medication } from '@/types/consultation';

interface ConsultationDetailsModalProps {
  appointment: Appointment;
  patientName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function ConsultationDetailsModal({
  appointment,
  patientName,
  onClose,
  onSuccess,
}: ConsultationDetailsModalProps) {
  const [consultation, setConsultation] = useState<ConsultationWithMedications | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Edit form state
  const [notes, setNotes] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [selectedMedications, setSelectedMedications] = useState<SelectedMedication[]>([]);

  useEffect(() => {
    loadConsultation();
  }, [appointment.id]);

  const loadConsultation = async () => {
    try {
      setIsLoading(true);
      const data = await consultationsService.getByAppointment(appointment.id);
      
      if (!data) {
        toast.error('Consulta não encontrada');
        onClose();
        return;
      }

      setConsultation(data);
      setNotes(data.notes || '');
      setDiagnosis(data.diagnosis || '');
      
      // Convert consultation medications to SelectedMedication format
      const converted: SelectedMedication[] = (data.medications || []).map(med => ({
        medication: {
          id: med.medicationId,
          name: med.medicationName || '',
          dosageMg: med.dosageMg || 0,
          frequencyHours: med.frequencyHours || 8,
          organizationId: '',
          createdAt: med.createdAt,
          updatedAt: med.createdAt,
        },
        durationDays: med.durationDays,
        instructions: med.instructions || '',
      }));
      setSelectedMedications(converted);
    } catch (error) {
      console.error('Error loading consultation:', error);
      toast.error('Erro ao carregar detalhes da consulta');
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!consultation) return;

    if (!notes.trim()) {
      toast.error('As anotações são obrigatórias');
      return;
    }

    setIsSaving(true);
    try {
      // Update consultation notes and diagnosis
      await consultationsService.update(consultation.id, {
        notes,
        diagnosis: diagnosis || undefined,
      });

      // Update medications
      await consultationsService.updateMedications(
        consultation.id,
        selectedMedications.map(sm => ({
          medicationId: sm.medication.id,
          durationDays: sm.durationDays,
          instructions: sm.instructions || undefined,
        }))
      );

      toast.success('Consulta atualizada com sucesso!');
      setIsEditing(false);
      await loadConsultation();
      onSuccess();
    } catch (error) {
      toast.error('Erro ao atualizar consulta');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (!consultation) return;
    setNotes(consultation.notes || '');
    setDiagnosis(consultation.diagnosis || '');
    // Reload from consultation data
    const converted: SelectedMedication[] = (consultation.medications || []).map(med => ({
      medication: {
        id: med.medicationId,
        name: med.medicationName || '',
        dosageMg: med.dosageMg || 0,
        frequencyHours: med.frequencyHours || 8,
        organizationId: '',
        createdAt: med.createdAt,
        updatedAt: med.createdAt,
      },
      durationDays: med.durationDays,
      instructions: med.instructions || '',
    }));
    setSelectedMedications(converted);
    setIsEditing(false);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <Modal isOpen={true} onClose={onClose} className="p-8">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="text-gray-700">Carregando detalhes...</span>
        </div>
      </Modal>
    );
  }

  if (!consultation) {
    return null;
  }

  return (
    <Modal isOpen={true} onClose={onClose} className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between rounded-t-xl">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6" />
              Detalhes da Consulta
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Consulta realizada em {formatDateTime(consultation.createdAt)}
            </p>
          </div>
          
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="mr-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Editar
            </button>
          )}
          
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Appointment Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-gray-700">
              <User className="w-5 h-5" />
              <span className="font-semibold">Paciente:</span>
              <span>{patientName}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Calendar className="w-5 h-5" />
              <span className="font-semibold">Data do Agendamento:</span>
              <span>{formatDateTime(appointment.startUtc)} - {new Date(appointment.endUtc).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            {appointment.description && (
              <div className="text-gray-700">
                <span className="font-semibold">Descrição:</span>
                <span className="ml-2">{appointment.description}</span>
              </div>
            )}
          </div>

          {/* Status Badge */}
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              ✓ Consulta Realizada
            </span>
            {consultation.completedAt && (
              <span className="text-sm text-gray-500">
                em {formatDateTime(consultation.completedAt)}
              </span>
            )}
          </div>

          {/* Notes Section */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Anotações da Consulta *
            </label>
            {isEditing ? (
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[150px]"
                placeholder="Descreva o que foi observado durante a consulta..."
              />
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-gray-700">
                {consultation.notes || 'Nenhuma anotação'}
              </div>
            )}
          </div>

          {/* Diagnosis Section */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Diagnóstico
            </label>
            {isEditing ? (
              <textarea
                value={diagnosis}
                onChange={(e) => setDiagnosis(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                placeholder="Diagnóstico médico (opcional)"
              />
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-gray-700">
                {consultation.diagnosis || 'Nenhum diagnóstico registrado'}
              </div>
            )}
          </div>

          {/* Medications Section */}
          {isEditing ? (
            <MedicationManager
              selectedMedications={selectedMedications}
              onMedicationsChange={setSelectedMedications}
              className="border-t pt-6"
            />
          ) : (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Medicamentos Prescritos
              </h3>
              <div className="space-y-3">
                {(!consultation.medications || consultation.medications.length === 0) ? (
                  <p className="text-gray-500 text-sm">Nenhum medicamento prescrito</p>
                ) : (
                  consultation.medications.map((med) => (
                    <div
                      key={med.id}
                      className="bg-blue-50 border border-blue-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-blue-900">
                            {med.medicationName}
                          </h4>
                          <p className="text-sm text-blue-700 mt-1">
                            Dosagem: {med.dosageMg}mg - A cada {med.frequencyHours}h
                          </p>
                          <p className="text-sm text-blue-700">
                            Duração: {med.durationDays} dias
                          </p>
                          {med.instructions && (
                            <div className="mt-2 text-sm text-gray-700 bg-white rounded p-2">
                              <span className="font-medium">Instruções:</span> {med.instructions}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          )}
        </div>
    </Modal>
  );
}
